import { PET_BALANCE, InteractionType, PlayActionType } from '../config/petBalance';
import {
  DungeonDifficulty,
  OriginalPetForm,
  PetAbilities,
  PetEvolutionState,
  PetNurtureTraits,
  PetProfile,
  PetStats
} from '../types';

export const clamp = (value: number, min = 0, max = 100) => Math.max(min, Math.min(max, value));

export const normalizeMood = (stats: Pick<PetStats, 'hunger' | 'energy' | 'health' | 'happiness'>): PetStats['mood'] => {
  if (stats.hunger >= 80) return 'hungry';
  if (stats.energy <= 20) return 'sleepy';
  if (stats.health <= 30) return 'sick';
  if (stats.happiness <= 30) return 'sad';
  return 'happy';
};

export const mergeStats = (currentStats: PetStats, delta: Partial<PetStats>): PetStats => {
  const merged: PetStats = {
    ...currentStats,
    hunger: clamp((currentStats.hunger ?? 0) + (delta.hunger || 0)),
    happiness: clamp((currentStats.happiness ?? 0) + (delta.happiness || 0)),
    energy: clamp((currentStats.energy ?? 0) + (delta.energy || 0)),
    health: clamp((currentStats.health ?? 0) + (delta.health || 0)),
    mood: currentStats.mood,
    lastUpdatedAt: new Date()
  };
  return { ...merged, mood: normalizeMood(merged) };
};

export const applyElapsedDecay = (stats: PetStats, now = new Date()): PetStats => {
  const last = stats.lastUpdatedAt ? new Date(stats.lastUpdatedAt) : now;
  const elapsedMin = Math.min(
    PET_BALANCE.maxDecayMinutes,
    Math.max(0, (now.getTime() - last.getTime()) / 60000)
  );
  if (elapsedMin <= 0) {
    return { ...stats, lastUpdatedAt: now, mood: normalizeMood(stats) };
  }
  const { decayPerMinute } = PET_BALANCE;
  const decayed: PetStats = {
    ...stats,
    hunger: clamp(stats.hunger + decayPerMinute.hunger * elapsedMin),
    energy: clamp(stats.energy - decayPerMinute.energy * elapsedMin),
    happiness: clamp(stats.happiness - decayPerMinute.happiness * elapsedMin),
    lastUpdatedAt: now
  };
  return { ...decayed, mood: normalizeMood(decayed) };
};

export const applyLevelUp = (current: PetProfile): PetProfile => {
  let level = current.level;
  let exp = current.exp;
  const multiplier = PET_BALANCE.levelExpMultiplier;
  while (exp >= level * multiplier) {
    exp -= level * multiplier;
    level += 1;
  }
  return { ...current, level, exp };
};

export const addNurtureTrait = (
  profile: PetProfile,
  trait: keyof PetNurtureTraits,
  amount = 1
): PetProfile => ({
  ...profile,
  nurtureTraits: {
    ...profile.nurtureTraits,
    [trait]: Math.max(0, Number(profile.nurtureTraits?.[trait] || 0) + amount)
  }
});

export const addPetAbility = (
  profile: PetProfile,
  ability: keyof PetAbilities,
  amount: number
): PetProfile => ({
  ...profile,
  abilities: {
    ...profile.abilities,
    [ability]: Math.min(100, Math.round((Number(profile.abilities?.[ability] || 1) + amount) * 10) / 10)
  }
});

export const applyInteraction = (
  type: InteractionType,
  profile: PetProfile,
  stats: PetStats
): { profile: PetProfile; stats: PetStats } => {
  const effect = PET_BALANCE.interaction[type];
  const nextStats = mergeStats(stats, effect.delta as Partial<PetStats>);
  let nextProfile = applyLevelUp({
    ...profile,
    exp: profile.exp + effect.exp,
    intimacy: clamp(profile.intimacy + effect.intimacy)
  });
  nextProfile = addNurtureTrait(nextProfile, type === 'chat' ? 'bond' : type === 'rest' ? 'care' : 'courage', 2);
  nextProfile = addPetAbility(nextProfile, type === 'chat' ? 'starlight' : type === 'rest' ? 'vitality' : 'courage', 0.1);
  return { profile: nextProfile, stats: nextStats };
};

export const getLocalDateKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const normalizeDailyActions = (profile: PetProfile): PetProfile => {
  const today = getLocalDateKey();
  const daily = profile.dailyActions;
  if (!daily || daily.date !== today) {
    return { ...profile, dailyActions: { date: today, strokeCount: 0, chatCount: 0, restCount: 0 } };
  }
  return {
    ...profile,
    dailyActions: {
      date: today,
      strokeCount: Number(daily.strokeCount || 0),
      chatCount: Number(daily.chatCount || 0),
      restCount: Number(daily.restCount || 0)
    }
  };
};

export const getStrokeFreeRemaining = (profile: PetProfile, limit = PET_BALANCE.freeStrokePerDay): number => {
  const normalized = normalizeDailyActions(profile);
  const used = normalized.dailyActions?.strokeCount ?? 0;
  return Math.max(0, limit - used);
};

export const getDailyInteractionRemaining = (
  profile: PetProfile,
  type: 'chat' | 'rest',
  limits = { chat: PET_BALANCE.freeChatPerDay, rest: PET_BALANCE.freeRestPerDay }
): number => {
  const normalized = normalizeDailyActions(profile);
  const used = type === 'chat'
    ? normalized.dailyActions?.chatCount ?? 0
    : normalized.dailyActions?.restCount ?? 0;
  const limit = limits[type];
  return Math.max(0, limit - used);
};

export const recordDailyInteraction = (profile: PetProfile, type: 'chat' | 'rest'): PetProfile => {
  const normalized = normalizeDailyActions(profile);
  const daily = normalized.dailyActions!;
  return {
    ...normalized,
    dailyActions: {
      ...daily,
      chatCount: daily.chatCount + (type === 'chat' ? 1 : 0),
      restCount: daily.restCount + (type === 'rest' ? 1 : 0)
    }
  };
};

export const applyPlayAction = (
  action: PlayActionType,
  profile: PetProfile,
  stats: PetStats,
  options?: { incrementStrokeCount?: boolean }
): { profile: PetProfile; stats: PetStats } => {
  const effect = PET_BALANCE.playActions[action];
  const nextStats = mergeStats(stats, effect.delta as Partial<PetStats>);
  let nextProfile = applyLevelUp({
    ...profile,
    exp: profile.exp + effect.exp,
    intimacy: clamp(profile.intimacy + effect.intimacy)
  });

  if (action === 'stroke' && options?.incrementStrokeCount) {
    const normalized = normalizeDailyActions(nextProfile);
    const today = getLocalDateKey();
    const count = normalized.dailyActions?.strokeCount ?? 0;
    nextProfile = {
      ...normalized,
      dailyActions: { ...normalized.dailyActions!, date: today, strokeCount: count + 1 }
    };
  }

  nextProfile = addNurtureTrait(nextProfile, action === 'stroke' ? 'bond' : 'courage', 2);
  nextProfile = addPetAbility(nextProfile, action === 'stroke' ? 'starlight' : 'courage', action === 'stroke' ? 0.1 : 0.2);

  return { profile: nextProfile, stats: nextStats };
};

const FINAL_FOR_BRANCH: Partial<Record<OriginalPetForm, OriginalPetForm>> = {
  starBean: 'starKeeper',
  cloudBean: 'cloudGuardian',
  flameBean: 'flameDragon'
};

const FORM_NAMES: Record<OriginalPetForm, string> = {
  starEgg: '별알',
  mongsil: '몽실',
  starBean: '별콩',
  cloudBean: '구름콩',
  flameBean: '불꽃콩',
  starKeeper: '별지기',
  cloudGuardian: '구름수호자',
  flameDragon: '불꽃용'
};

export const getDominantBranch = (profile: PetProfile): 'star' | 'cloud' | 'flame' => {
  const { bond = 0, care = 0, courage = 0 } = profile.nurtureTraits || {};
  if (care > bond && care >= courage) return 'cloud';
  if (courage > bond && courage > care) return 'flame';
  return 'star';
};

export const getNextEvolution = (profile: PetProfile): OriginalPetForm | null => {
  if (profile.petForm === 'starEgg') return 'mongsil';
  if (profile.petForm === 'mongsil') {
    const branch = getDominantBranch(profile);
    return branch === 'cloud' ? 'cloudBean' : branch === 'flame' ? 'flameBean' : 'starBean';
  }
  return FINAL_FOR_BRANCH[profile.petForm] || null;
};

export const canEvolve = (profile: PetProfile, evolution: PetEvolutionState): boolean => {
  const next = getNextEvolution(profile);
  if (!next) return false;
  const { evolution: rules } = PET_BALANCE;
  if (profile.petForm === 'starEgg') {
    return profile.level >= rules.hatch.level && profile.intimacy >= rules.hatch.intimacy;
  }
  if (profile.petForm === 'mongsil') {
    return profile.level >= rules.branch.level && profile.intimacy >= rules.branch.intimacy;
  }
  return (
    profile.level >= rules.final.level &&
    profile.intimacy >= rules.final.intimacy &&
    evolution.dungeonClearCount >= rules.final.dungeonClearCount
  );
};

export const applyEvolution = (
  profile: PetProfile,
  evolution: PetEvolutionState
): { profile: PetProfile; evolution: PetEvolutionState } | null => {
  const next = getNextEvolution(profile);
  if (!next || !canEvolve(profile, evolution)) return null;
  const now = new Date();
  const usesDefaultName = profile.name === FORM_NAMES[profile.petForm];
  return {
    profile: { ...profile, petForm: next, name: usesDefaultName ? FORM_NAMES[next] : profile.name },
    evolution: {
      ...evolution,
      lastEvolvedAt: now,
      history: [...evolution.history, { from: profile.petForm, to: next, at: now }]
    }
  };
};

export const getDungeonEnergyCost = (difficulty: DungeonDifficulty): number =>
  PET_BALANCE.dungeonEnergy[difficulty];

export const getEvolutionStatus = (profile: PetProfile, evolution: PetEvolutionState) => {
  const { evolution: rules } = PET_BALANCE;
  const nextForm = getNextEvolution(profile);
  const isEgg = profile.petForm === 'starEgg';
  const isBaby = profile.petForm === 'mongsil';
  const requirements = isEgg ? rules.hatch : isBaby ? rules.branch : rules.final;
  return {
    nextForm,
    dominantBranch: getDominantBranch(profile),
    requiredLevel: requirements.level,
    requiredIntimacy: requirements.intimacy,
    requiredDungeonClearCount: 'dungeonClearCount' in requirements ? requirements.dungeonClearCount : 0,
    satisfied: canEvolve(profile, evolution)
  };
};
